import { noteServerTime } from "./serverClock";
import { announceChange } from "./liveRefresh";

function getToken() {
  return localStorage.getItem("token");
}

// The backend answers every error with a JSON `detail` (see its exception
// handlers), so that is what we show. A body with no `detail` means the
// failure happened before the app saw the request — nginx, the ingress, a
// dead pod — and there the status line is the only thing that says which,
// so keep statusText rather than showing a bare number.
function httpError(status, statusText, data) {
  // `detail` is usually a sentence, but a few endpoints answer with an object
  // when the caller can act on the refusal — the price import returns the
  // service names it did not recognise so they can be reviewed and created.
  // new Error({...}) renders as "[object Object]", so the message comes from
  // `detail.message` and the whole structure is carried on `err.detail`.
  const detail = data && data.detail;
  const message = (detail && typeof detail === "object")
    ? (detail.message || `HTTP ${status}${statusText ? ` ${statusText}` : ""}`)
    : detail;
  const err = message
    ? new Error(message)
    : new Error(`HTTP ${status}${statusText ? ` ${statusText}` : ""}`);
  err.detail = detail;
  // Carried so a caller can tell a transient failure from a rejected request.
  // The chunked upload retries a 503 (a storage hiccup mid-transfer, worth
  // another go because the parts are still held) and never retries a 4xx, which
  // would repeat a request the server has already judged wrong.
  err.status = status;
  return err;
}

/**
 * A 401 answered to a request that *did* carry a token — i.e. the sign-in
 * session ended, as opposed to a credential being wrong.
 *
 * It exists because the two need opposite handling and used to be one branch:
 * see the `handleUnauthorized` comment below.
 */
export class AuthExpiredError extends Error {
  constructor(message) {
    super(message);
    this.name        = "AuthExpiredError";
    this.status      = 401;
    this.authExpired = true;
  }
}

// ── Sign-out, and why it is not just a redirect ──────────────────────────────
//
// This used to be `window.location.href = "/login"` inline at each 401, which
// caused two separate incidents:
//
//   * JOB-529B91 — a full document navigation aborts every in-flight XHR, so
//     the once-a-minute problem poll hitting a 401 tore down an upload that
//     had been running for eight hours.
//   * JOB-C58EB8 — worse, because the redirect was paired with `return`
//     instead of a throw: the caller received `undefined` from a function it
//     was awaiting, so a 21-hour transfer that had *succeeded* reported
//     nothing at all. See CLAUDE.md.
//
// So: the token is cleared immediately (that part was never negotiable), but
// *navigating away* is a UI decision, handed to whoever owns the router, and
// deferrable by a caller that is in the middle of something it must not lose.

// The fallback, before AuthProvider registers its own: a navigation to the
// sign-in screen carrying the page the session ended on, as ProtectedRoute's
// redirect does, so signing in again returns there. Not when that page is the
// sign-in screen itself, which would nest one `next` inside another.
function signInAgain() {
  const { pathname, search } = window.location;
  if (pathname === "/login") { window.location.href = "/login"; return; }
  window.location.href = `/login?next=${encodeURIComponent(pathname + search)}`;
}

let sessionExpiredHandler = signInAgain;
let deferDepth   = 0;
let signOutPending = false;

/** Registered by AuthProvider so sign-out clears React state and routes. */
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

/**
 * Hold off the navigation half of sign-out for the duration of some work.
 *
 * Returns a release function. Call `release({ handled: true })` when you have
 * shown the user something actionable and do not want to be navigated away
 * from it; plain `release()` performs the sign-out that was deferred.
 */
export function deferSignOut() {
  deferDepth += 1;
  let released = false;
  return function release({ handled = false } = {}) {
    if (released) return;
    released = true;
    deferDepth -= 1;
    if (handled) signOutPending = false;
    if (deferDepth === 0 && signOutPending) {
      signOutPending = false;
      sessionExpiredHandler();
    }
  };
}

/**
 * Turn a 401 into the right Error, and sign out only when it really is one.
 *
 * `hadToken` is the whole distinction. A request made with no token in hand —
 * from the sign-in page or another public one — can be answered 401 for its
 * own reasons: treating that as an expiry signs a user out of a session they
 * never had, and replaces the server's sentence with a message about expiry.
 * So a 401 to a request that carried no credential is just an error like any
 * other.
 */
function handleUnauthorized(hadToken, data, status, statusText) {
  if (!hadToken) return httpError(status, statusText, data);

  localStorage.removeItem("token");
  if (deferDepth > 0) {
    signOutPending = true;
  } else {
    sessionExpiredHandler();
  }
  return new AuthExpiredError(
    data && data.detail
      ? `${data.detail} Your sign-in session has ended — sign in again to continue.`
      : "Your sign-in session has ended — sign in again to continue.",
  );
}

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const init = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(path, init);
  // Every response carries a Date header, and it is the server's clock at the
  // moment it answered — unlike a row timestamp, it cannot be stale. See
  // serverClock.js for why the difference matters.
  noteServerTime(res.headers?.get?.("Date"));

  let data;
  try { data = await res.json(); } catch { data = {}; }

  if (res.status === 401) throw handleUnauthorized(!!token, data, res.status, res.statusText);
  if (!res.ok) throw httpError(res.status, res.statusText, data);
  // After a change, the views showing shared data re-read (liveRefresh.js).
  // Never under /auth/: a re-read racing a sign-out reads as somebody else.
  if (method !== "GET" && !path.startsWith("/auth/")) announceChange({ method, path });
  return data;
}

export const api = {
  get:    (path)        => request("GET",    path),
  post:   (path, body)  => request("POST",   path, body),
  patch:  (path, body)  => request("PATCH",  path, body),
  // PUT is a full replacement, and both its callers mean exactly that: the
  // project's tags and its answers to the group's fields are sent whole.
  put:    (path, body)  => request("PUT",    path, body),
  delete: (path)        => request("DELETE", path),

  upload: async (path, formData) => {
    const headers = {};
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(path, { method: "POST", headers, body: formData });
    noteServerTime(res.headers?.get?.("Date"));
    let data;
    try { data = await res.json(); } catch { data = {}; }
    if (res.status === 401) throw handleUnauthorized(!!token, data, res.status, res.statusText);
    if (!res.ok) throw httpError(res.status, res.statusText, data);
    announceChange({ method: "POST", path });
    return data;
  },

  // Streams a File's raw bytes as the request body (via XHR, so we get real
  // upload progress events) instead of wrapping it in multipart/form-data.
  // The backend reads this as a raw stream straight into S3, so a file of
  // any size — including multi-terabyte — never has to fit in memory on
  // either end.
  // onStart receives the XHR itself, which is the only way a caller can abort
  // an upload already in flight — needed for the wizard's Cancel button.
  uploadStream: (path, file, headers, onProgress, onStart) => {
    const token = getToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", path);
      if (onStart) onStart(xhr);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = e => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
          return;
        }
        let data = {};
        try { data = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
        // Resolving here instead of rejecting was the worst of the three: the
        // wizard pushed `undefined` into its uploaded[] list, which serialises
        // to null and reaches submit_finish's md5.sum join as a TypeError.
        if (xhr.status === 401) {
          reject(handleUnauthorized(!!token, data, xhr.status, xhr.statusText));
          return;
        }
        reject(httpError(xhr.status, xhr.statusText, data));
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.onabort = () => reject(new Error("Upload cancelled"));
      xhr.send(file);
    });
  },
};

// One slice of a file, rather than the whole thing in a single request.
//
// The difference is not efficiency, it is survivability: a whole-file PUT of
// 883 GiB is one HTTP request that has to outlive the sign-in session, an
// ingress reload and every network blip for 21 hours, and when it dies the
// backend discards every byte. A slice is seconds long, so anything that kills
// it costs one slice — and the server keeps the parts, so the transfer picks up
// where it stopped. See CLAUDE.md, JOB-529B91 / JOB-E511C4 / JOB-C58EB8.
//
// Shares uploadStream's XHR machinery for the same reason: fetch() has no
// upload progress events.
api.uploadPart = (path, blob, headers, onProgress, onStart) =>
  api.uploadStream(path, blob, headers, onProgress, onStart);

// ── Authenticated files ───────────────────────────────────────────────────────
//
// A PDF, an .xlsx or a .sql dump cannot be reached with `<a href="/api/…">` or
// `window.location.href`. Both are *document navigations*, and a navigation
// carries no header this app controls — our token lives in localStorage, not in
// a cookie. The request therefore arrives with no Authorization at all, which
// is not "expired" but "absent": FastAPI's OAuth2PasswordBearer refuses it
// before any of our code runs, with its own `{"detail":"Not authenticated"}`,
// and the browser renders that JSON where the document should be.
//
// That is not a hypothetical. It shipped on five buttons — both document PDFs,
// the quotation PDF, the statistics export and the database backup — from one
// shared belief, written out in a comment that said the token "rides along
// because this is a same-origin navigation from an authenticated page". It does
// not. Only a request *we* build can carry it, so these fetch the bytes and
// hand the browser a blob it already has.
//
// The second thing this buys is that a 401 now goes through handleUnauthorized
// like every other call. Before, an expired session painted JSON on the screen
// and the app never learned it had ended.

/** How long a blob URL stays alive after it has been handed over.
 *  Revoking immediately races the tab that is still loading it and shows a
 *  blank viewer; never revoking holds the file in memory until the page does. */
const BLOB_TTL_MS = 60_000;

/** The server names the file (Content-Disposition); a blob otherwise inherits
 *  a meaningless name from the object URL. Falls back to the last path segment
 *  so a missing header degrades to something recognisable rather than to
 *  "download". */
function filenameFrom(disposition, path) {
  if (disposition) {
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8) {
      try { return decodeURIComponent(utf8[1]); } catch { /* fall through */ }
    }
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    if (plain) return plain[1];
  }
  return path.split("?")[0].split("/").pop() || "download";
}

/** GET a file with the session token attached. Errors match `request()`. */
async function fetchFile(path) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(path, { headers });
  noteServerTime(res.headers?.get?.("Date"));

  if (!res.ok) {
    // The error body is JSON even when the success body is not.
    let data;
    try { data = await res.json(); } catch { data = {}; }
    if (res.status === 401) throw handleUnauthorized(!!token, data, res.status, res.statusText);
    throw httpError(res.status, res.statusText, data);
  }
  return {
    blob: await res.blob(),
    filename: filenameFrom(res.headers?.get?.("Content-Disposition"), path),
  };
}

api.fetchFile = fetchFile;

/**
 * Show a PDF in a new tab — the portal's own viewer page, `/view?path=…`.
 *
 * Until 2026-10-06 this pointed a blank tab at a `blob:` URL: the browser's
 * viewer and nothing else, no way back to the portal, and a reload that failed
 * once the blob was revoked a minute later. The viewer page (PdfViewPage) is a
 * signed-in portal page that fetches the file itself with the token, so the
 * tab has the header, the document's name, Back to portal and Download, and a
 * reload fetches it again. Not `noopener`: Back to portal closes the tab and
 * returns to the opener when there is one.
 *
 * `window.open` stays the first statement, before any await — Safari and
 * Chrome's blocker tie it to the user gesture, which an await would spend.
 */
api.openFile = async (path) => {
  const tab = window.open(`/view?path=${encodeURIComponent(path)}`, "_blank");
  if (tab) return;
  // Popups blocked. Saving is a worse answer than showing, but it is a far
  // better one than navigating this tab away from the app.
  const file = await fetchFile(path);
  const url = URL.createObjectURL(file.blob);
  triggerSave(url, file.filename);
  setTimeout(() => URL.revokeObjectURL(url), BLOB_TTL_MS);
};

/** Save a file to disk — for the formats a browser cannot display. */
api.saveFile = async (path) => {
  const { blob, filename } = await fetchFile(path);
  const url = URL.createObjectURL(blob);
  triggerSave(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), BLOB_TTL_MS);
};

function triggerSave(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
